import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '../store/auth';
import { useScope } from '../store/scope';
import { useParticipations } from './api';
import { initials } from '../lib/format';

const NAV = [
  { to: '/admin', label: 'Dashboard', end: true, icon: 'M4 13h6V4H4zM14 20h6V4h-6zM4 20h6v-5H4z' },
  { to: '/admin/participations', label: 'Expo Participations', icon: 'M3 7h18M3 12h18M3 17h18' },
  { to: '/admin/jewellery', label: 'Jewellery', icon: 'M12 3l3 4 6 1-4 4 1 6-6-3-6 3 1-6-4-4 6-1z' },
  { to: '/admin/import', label: 'Import Data', icon: 'M12 3v12m0 0l-4-4m4 4l4-4M4 21h16' },
  { to: '/admin/assignments', label: 'Product Assignments', icon: 'M4 7h10M4 12h16M4 17h10' },
  { to: '/admin/bookings', label: 'Bookings', icon: 'M6 4h12v16l-6-3-6 3z' },
  { to: '/admin/visitors', label: 'Visitors', icon: 'M16 14a4 4 0 10-8 0M12 3a4 4 0 100 8 4 4 0 000-8z' },
  { to: '/admin/analytics', label: 'Analytics', icon: 'M4 19h16M7 16V9M12 16V5M17 16v-4' },
  { to: '/admin/reports', label: 'Reports', icon: 'M7 3h7l5 5v13H7zM14 3v5h5' },
  { to: '/admin/qr-codes', label: 'QR Codes', icon: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z' },
  { to: '/admin/users', label: 'Users', icon: 'M16 14a4 4 0 10-8 0M12 3a4 4 0 100 8 4 4 0 000-8z' },
  { to: '/admin/audit-logs', label: 'Audit Logs', icon: 'M9 12h6M9 16h6M9 8h6M5 3h14v18H5z' },
  { to: '/admin/settings', label: 'Settings', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19 12a7 7 0 00-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 00-1.7-1L14 2h-4l-.8 2.9a7 7 0 00-1.7 1l-2.4-1-2 3.4L3 10a7 7 0 000 4l-2 1.6 2 3.4 2.4-1a7 7 0 001.7 1L10 22h4l.8-2.9a7 7 0 001.7-1l2.4 1 2-3.4-2-1.6c.1-.3.1-.7.1-1z' },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const { participationId, set } = useScope();
  const { data: parts } = useParticipations();
  const [mobileNav, setMobileNav] = useState(false);

  const list = parts?.data ?? [];

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-60 flex-col border-r border-ink-100 bg-white transition-transform lg:static lg:flex lg:translate-x-0',
          mobileNav ? 'flex translate-x-0' : 'hidden -translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-ink-100 px-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gold-500 text-white">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 3l3 4 6 1-4 4 1 6-6-3-6 3 1-6-4-4 6-1z" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-serif text-sm font-semibold">Exhibition Console</span>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileNav(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm',
                  isActive ? 'bg-ink-900 text-white' : 'text-ink-600 hover:bg-ink-100',
                )
              }
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d={n.icon} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-ink-100 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-900 text-xs font-semibold text-white">
              {user ? initials(user.name) : '–'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs text-ink-400">{user?.role}</p>
            </div>
            <button
              className="text-ink-400 hover:text-ink-700"
              title="Sign out"
              onClick={() => logout().then(() => navigate('/admin/login'))}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M16 17l5-5-5-5M21 12H9M12 19H5V5h7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden" onClick={() => setMobileNav(false)} />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-ink-100 bg-white/95 px-4 backdrop-blur">
          <button className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 lg:hidden" onClick={() => setMobileNav(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs font-medium uppercase tracking-wide text-ink-400 sm:block">Expo focus</span>
            <select
              className="input h-8 w-auto max-w-[16rem] py-1 text-sm"
              value={participationId ?? ''}
              onChange={(e) => set(e.target.value || null)}
            >
              <option value="">All expos</option>
              {list.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.expoName} · {p.status}
                </option>
              ))}
            </select>
          </div>
          <div className="ml-auto" />
        </header>

        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
